import type {
  AudienceSegmentEntry,
  CumulativeBundleSegment,
  CumulativeDomainSegment,
  InventoryBundle,
  InventoryDomain,
} from '@openx/types/gqlTypes.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { noop } from '@openx/utils/noop.ts';
import { createContext, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import {
  type DealPromptState,
  type DealPromptTileDetailsPayload,
  type DealPromptTileDetailsPayloadForAudience,
  promptModes,
  type ViewOption,
  ViewOptions,
} from '../../constants.ts';
import { dealPromptContextTypes } from '../types.ts';
import { convertDealPromptDataToDeal } from './utils/convertDealPromptDataToDeal.ts';

export const initialState: DealPromptState = {
  prompt: '',
  contextType: dealPromptContextTypes.DOMAINS,
  activeViewType: ViewOptions.MAIN,
  mode: promptModes.DEFAULT_MODE,
  lastSubmittedPrompt: '',
  lastSubmittedQueries: [],
  lastSubmittedContextType: dealPromptContextTypes.DOMAINS,
  lastSubmittedFilterCriteria: { filters: {} },
  loading: false,
  firstLoadPerformed: false,
  requestAborted: false,
  selectedItems: [],
  selectedAudienceItems: [],
  hasError: false,
  hasDifferentFiltersThanWissp: false,
};

export type DealPromptContextProps = {
  state: DealPromptState;
  handleBack: () => void;
  openDetails: (payload: DealPromptTileDetailsPayload) => void;
  openDetailsForAudience: (payload: DealPromptTileDetailsPayloadForAudience) => void;
  createDeal: (dealInitialData: CumulativeDomainSegment | CumulativeBundleSegment) => void;
  setSelectedItems: (items: InventoryDomain[] | InventoryBundle[]) => void;
  setSelectedAudienceItems: (items: AudienceSegmentEntry[]) => void;
  setLoading: (value: DealPromptState['loading']) => void;
  setRequestAborted: (value: DealPromptState['requestAborted']) => void;
  setFirstLoadPerformed: (value: DealPromptState['firstLoadPerformed']) => void;
  setLastSubmittedPrompt: (value: DealPromptState['lastSubmittedPrompt']) => void;
  setLastSubmittedQueries: (value: DealPromptState['lastSubmittedQueries']) => void;
  setLastSubmittedFilterCriteria: (value: DealPromptState['lastSubmittedFilterCriteria']) => void;
  setLastSubmittedContextType: (value: DealPromptState['lastSubmittedContextType']) => void;
  setMode: (value: DealPromptState['mode']) => void;
  setContextType: (value: DealPromptState['contextType']) => void;
  setPrompt: (value: DealPromptState['prompt']) => void;
  setHasError: (value: DealPromptState['hasError']) => void;
  setHasDifferentFiltersThanWissp: (value: DealPromptState['hasDifferentFiltersThanWissp']) => void;
};

const initialDealPromptContextValues: DealPromptContextProps = {
  setLoading: noop,
  setRequestAborted: noop,
  setFirstLoadPerformed: noop,
  setLastSubmittedPrompt: noop,
  setLastSubmittedQueries: noop,
  setLastSubmittedFilterCriteria: noop,
  setLastSubmittedContextType: noop,
  setMode: noop,
  setContextType: noop,
  setPrompt: noop,
  handleBack: noop,
  openDetails: noop,
  createDeal: noop,
  setSelectedItems: noop,
  setHasError: noop,
  setHasDifferentFiltersThanWissp: noop,
  openDetailsForAudience: noop,
  setSelectedAudienceItems: noop,
  state: initialState,
};

export const DealPromptContext = createContext(initialDealPromptContextValues);

export const DealPromptProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewType, setViewType] = useState<ViewOption>(ViewOptions.MAIN);
  const [selectedTile, setSelectedTile] = useState<
    DealPromptTileDetailsPayload | DealPromptTileDetailsPayloadForAudience | undefined
  >(undefined);
  const [selectedItems, setSelectedItems] = useState<InventoryDomain[] | InventoryBundle[]>([]);
  const [selectedAudienceItems, setSelectedAudienceItems] = useState<AudienceSegmentEntry[]>([]);
  const [loading, setLoading] = useState<DealPromptState['loading']>(initialState.loading);
  const [requestAborted, setRequestAborted] = useState<DealPromptState['requestAborted']>(initialState.requestAborted);
  const [firstLoadPerformed, setFirstLoadPerformed] = useState<DealPromptState['firstLoadPerformed']>(
    initialState.firstLoadPerformed
  );
  const [lastSubmittedPrompt, setLastSubmittedPrompt] = useState<DealPromptState['lastSubmittedPrompt']>(
    initialState.lastSubmittedPrompt
  );
  const [lastSubmittedQueries, setLastSubmittedQueries] = useState<DealPromptState['lastSubmittedQueries']>(
    initialState.lastSubmittedQueries
  );
  const [lastSubmittedFilterCriteria, setLastSubmittedFilterCriteria] = useState<
    DealPromptState['lastSubmittedFilterCriteria']
  >(initialState.lastSubmittedFilterCriteria);
  const [lastSubmittedContextType, setLastSubmittedContextType] = useState<DealPromptState['lastSubmittedContextType']>(
    initialState.lastSubmittedContextType
  );
  const [mode, setMode] = useState<DealPromptState['mode']>(initialState.mode);
  const [contextType, setContextType] = useState<DealPromptState['contextType']>(initialState.contextType);
  const [prompt, setPrompt] = useState<DealPromptState['prompt']>(initialState.prompt);
  const [hasError, setHasError] = useState<DealPromptState['hasError']>(initialState.hasError);
  const [hasDifferentFiltersThanWissp, setHasDifferentFiltersThanWissp] = useState(false);
  const { t } = useTranslation();

  const openDetails = (payload: DealPromptTileDetailsPayload) => {
    setSelectedTile(payload);
    setViewType(ViewOptions.DETAILS);
    setSelectedItems((payload.item?.results?.filter(d => !!d) as InventoryDomain[] | InventoryBundle[]) || []);
  };

  const openDetailsForAudience = (payload: DealPromptTileDetailsPayloadForAudience) => {
    setSelectedTile(payload);
    setViewType(ViewOptions.DETAILS);
    const mappedSegments = payload.item?.segments
      ?.filter(d => !!d)
      .map(d => ({
        ...d,
        account_name: d.account_name === 'OpenAudience' ? 'OpenXSelect' : d.account_name,
      }));
    setSelectedAudienceItems(mappedSegments || []);
  };

  const createDeal = (dsSetItem: CumulativeDomainSegment | CumulativeBundleSegment) => {
    const dsInitialDealFormPreset = convertDealPromptDataToDeal(
      dsSetItem,
      lastSubmittedFilterCriteria,
      contextType,
      selectedItems
    );
    const targetingKeys = Object.keys(dsInitialDealFormPreset || {});
    const hasTargetingApplied = targetingKeys.some(key => key !== TargetingFields.DIMENSION);

    // This clears query params, so coming back via browser back button goes to clean form
    setSearchParams({}, { replace: true });
    navigate('/deals/create', {
      state: {
        dsInitialDealFormPreset,
        ...(hasTargetingApplied && {
          fromScreenInfoConfig: {
            message: t('From Deal Prompt'),
            messageFieldNames: targetingKeys,
            messageTitleName: t('Create Deal'),
          },
        }),
        useAsTemplate: true,
      },
    });
  };

  const handleBack = () => {
    setSelectedItems([]);
    setSelectedAudienceItems([]);
    setSelectedTile(undefined);
    setViewType(ViewOptions.MAIN);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dom query param only on initial load
  useEffect(() => {
    const plaformFilterParam = searchParams.get('platform');

    if (plaformFilterParam) {
      setContextType('apps');
    }
  }, []);

  return (
    <DealPromptContext.Provider
      value={{
        state: {
          activeViewType: viewType,
          selectedTile,
          selectedItems,
          selectedAudienceItems,
          loading,
          requestAborted,
          firstLoadPerformed,
          lastSubmittedPrompt,
          lastSubmittedQueries,
          lastSubmittedFilterCriteria,
          lastSubmittedContextType,
          mode,
          contextType,
          prompt,
          hasError,
          hasDifferentFiltersThanWissp,
        },
        setLoading,
        setRequestAborted,
        setFirstLoadPerformed,
        setLastSubmittedPrompt,
        setLastSubmittedQueries,
        setLastSubmittedFilterCriteria,
        setLastSubmittedContextType,
        setMode,
        setContextType,
        setPrompt,
        handleBack,
        openDetails,
        createDeal,
        setSelectedItems,
        openDetailsForAudience,
        setSelectedAudienceItems,
        setHasError,
        setHasDifferentFiltersThanWissp,
      }}
    >
      {children}
    </DealPromptContext.Provider>
  );
};
